import {
  Badge,
  Box,
  Button,
  Flex,
  List,
  ListIcon,
  ListItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Article, articlesApi } from '@texas/api/endpoints/articlesApi';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { Icons } from '@texas/components/shared/Icons';
import { reactEvents } from '@bridge/reactEvents';
import { ManagementCard } from './ManagementCard';
import { useTranslation } from 'react-i18next';

export function MigrationManagement({
  article,
  onUpdated,
}: {
  article: Article;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const migrate = useDisclosure();
  const downgrade = useDisclosure();
  const isV2 = article.version === 2;
  const complete = () => {
    reactEvents.articleVersionUpdated.dispatch();
    onUpdated();
  };

  return (
    <>
      <Flex justify="flex-end" gap={3} mb={5}>
        <Button
          variant="texas-solid"
          onClick={isV2 ? downgrade.onOpen : migrate.onOpen}
        >
          {t(
            isV2
              ? 'article.migration.downgradeToV0'
              : 'article.migration.migrateToV2',
          )}
        </Button>
      </Flex>
      <ManagementCard
        title={t('article.migration.versionMigration')}
        description={t('article.management.versionDescription')}
        action={
          <Badge colorScheme="green">
            {t(
              isV2
                ? 'article.management.v2Active'
                : 'article.management.readyToMigrate',
            )}
          </Badge>
        }
      >
        <List spacing={4}>
          <ListItem>
            <ListIcon as={Icons.Checkmark} color="status.success" />
            {t('article.management.variantsMapped')}
          </ListItem>
          <ListItem>
            <ListIcon as={Icons.Checkmark} color="status.success" />
            {t('article.management.branchesConfigured')}
          </ListItem>
          <ListItem>
            <ListIcon as={Icons.Checkmark} color="status.success" />
            {t('article.management.usersAssigned')}
          </ListItem>
        </List>
        <Box
          bg="status.dangerBackground"
          border="1px solid"
          borderColor="status.dangerBorder"
          p={4}
          mt={6}
        >
          <Text color="status.danger" fontSize="sm">
            {t('article.management.migrationWarning')}
          </Text>
        </Box>
      </ManagementCard>
      <VerifyDialogWithRequest
        headerTitle={t('article.migration.migrateToV2')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('article.management.confirmMigration')}
        primaryRequest={articlesApi.migrateToV2}
        args={[article.id]}
        isOpen={migrate.isOpen}
        onClose={migrate.onClose}
        onPerformed={complete}
        onSuccessTitle={t('article.migration.migrationSuccess')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('article.migration.downgradeToV0')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('article.management.confirmDowngrade')}
        primaryRequest={articlesApi.downgradeToV0}
        args={[article.id]}
        isOpen={downgrade.isOpen}
        onClose={downgrade.onClose}
        onPerformed={complete}
        onSuccessTitle={t('article.migration.downgradeSuccess')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
    </>
  );
}
